import type { DashboardMatchBrief, DashboardMatchParticipantBrief, RetoSender } from "./api";

// «Tu próximo reto» (Eduardo 2026-10-03): en un torneo sin calendario de
// canchas los jugadores coordinan entre ellos, así que el reto siempre ofrece
// el celular del rival (WhatsApp y Llamar). «Reportar resultado» sale solo si
// el torneo deja reportar a los jugadores; si no, este aviso.
export const RESULT_BY_ORGANIZER_NOTE = "El resultado lo captura el organizador.";

export type RetoContact = {
  name: string | null;
  /** Como lo guardó el organizador (para mostrar). */
  phone: string;
  /** Solo dígitos (para wa.me y tel:). */
  digits: string;
  /** Eduardo 2026-10-04: el rival es menor sin celular propio y el número es
   * el de su papá, mamá o tutor. */
  guardian: boolean;
};

/** «Tel. del papá, mamá o tutor de Isaac» (Eduardo 2026-10-04). */
export function guardianPhoneLabel(name?: string | null): string {
  const clean = (name ?? "").trim();
  return clean ? `Tel. del papá, mamá o tutor de ${clean}` : "Tel. del papá, mamá o tutor";
}

export function cleanPhone(value?: string | null): string | null {
  if (!value) return null;
  const digits = value.replace(/\D/g, "");
  return digits || null;
}

export function whatsappHref(phone: string, message: string): string {
  const digits = phone.length === 10 ? `52${phone}` : phone;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

export function firstName(name?: string | null): string {
  return (name ?? "").trim().split(/\s+/)[0] || "Rival";
}

export function isTeamSide(side?: DashboardMatchParticipantBrief | null): boolean {
  return side?.participant_kind === "team" || side?.team_id != null;
}

/** Lado del jugador que mira. En dobles los dos lados vienen sin perfil
 * (`player_profile_id` null): manda `viewer_participant_id` (o el
 * participante propio); el perfil solo para respuestas viejas. */
export function retoOwnSide(
  match: DashboardMatchBrief | null,
  { profileId, ownParticipantIds }: { profileId?: number | null; ownParticipantIds?: Array<number | null | undefined> } = {},
): DashboardMatchParticipantBrief | null {
  if (!match) return null;
  const sides = [match.player1, match.player2];
  const viewer = match.viewer_participant_id;
  if (viewer != null) {
    const own = sides.find((side) => side?.participant_id === viewer);
    if (own) return own;
  }
  const ownIds = (ownParticipantIds ?? []).filter((id): id is number => id != null);
  if (ownIds.length) {
    const own = sides.find((side) => side != null && ownIds.includes(side.participant_id));
    if (own) return own;
  }
  if (profileId) {
    const own = sides.find((side) => side?.player_profile_id === profileId);
    if (own) return own;
  }
  return null;
}

/** El rival del reto (el otro lado del partido). */
export function retoRival(
  match: DashboardMatchBrief | null,
  options: { profileId?: number | null; ownParticipantIds?: Array<number | null | undefined> } = {},
): DashboardMatchParticipantBrief | null {
  if (!match) return null;
  const own = retoOwnSide(match, options);
  if (own) return own === match.player1 ? match.player2 : match.player1;
  return match.player2 ?? match.player1 ?? null;
}

/** Celulares del rival que manda el servidor (solo cuando se comparten). Un
 * número repetido sale una vez; si es a la vez el del tutor de un menor y el
 * propio de otro integrante (pareja de papá e hijo), se queda el propio: el
 * papá también es rival y se le habla por su nombre. */
export function retoRivalContacts(rival?: DashboardMatchParticipantBrief | null): RetoContact[] {
  if (!rival) return [];
  const raw = rival.contacts?.length
    ? rival.contacts.map((contact) => ({
        name: contact.display_name,
        phone: contact.phone,
        guardian: contact.is_guardian === true,
      }))
    : rival.phone
      ? [{ name: rival.display_name, phone: rival.phone, guardian: false }]
      : [];
  const indexByDigits = new Map<string, number>();
  const contacts: RetoContact[] = [];
  for (const contact of raw) {
    const digits = cleanPhone(contact.phone);
    if (!digits) continue;
    const entry = { name: contact.name ?? null, phone: contact.phone.trim(), digits, guardian: contact.guardian };
    const index = indexByDigits.get(digits);
    if (index === undefined) {
      indexByDigits.set(digits, contacts.length);
      contacts.push(entry);
    } else if (contacts[index].guardian && !entry.guardian) {
      contacts[index] = entry;
    }
  }
  return contacts;
}

/** ¿El reto ofrece contacto? Sin calendario de canchas siempre; con
 * calendario, solo si los jugadores reportan el resultado (como antes). */
export function retoShowsContact(match: DashboardMatchBrief | null): boolean {
  if (!match) return false;
  return Boolean(match.allow_player_result_reporting) || match.uses_court_calendar === false;
}

export function retoContactLine(rival?: DashboardMatchParticipantBrief | null): string {
  const contacts = retoRivalContacts(rival);
  if (!contacts.length) return "Coordina directo con tu rival.";
  if (isTeamSide(rival)) {
    const entries = contacts.map((contact) =>
      contact.guardian
        ? `${guardianPhoneLabel(firstName(contact.name))}: ${contact.phone}`
        : `${firstName(contact.name)} ${contact.phone}`,
    );
    return `Coordina directo con tus rivales: ${entries.join(" · ")}`;
  }
  const contact = contacts[0];
  if (contact.guardian) {
    return `Coordina directo con tu rival. ${guardianPhoneLabel(contact.name ?? rival?.display_name)}: ${contact.phone}`;
  }
  return `Coordina directo con tu rival: ${contact.phone}`;
}

/** Nombre corto de quien escribe: primera palabra del nombre + primera del
 * apellido, con los campos ESTRUCTURADOS del perfil propio elegido
 * ({first_name: "Juan Pablo", last_name: "Pérez"} → «Juan Pérez»; el
 * apellido respeta partículas: «De la Garza López» → «De la Garza»). Nunca se
 * deduce de un nombre completo ni de la etiqueta de una pareja («Lucia /
 * Beto»): contar palabras fallaba con nombres compuestos (PR #581, Torni).
 * Sin apellido, solo el nombre; sin ninguno, null. */
export function retoSenderName(sender?: RetoSender | null): string | null {
  const words = (value: string | null | undefined) => (value ?? "").trim().split(/\s+/).filter(Boolean);
  const parts = [words(sender?.first_name)[0] ?? "", retoFirstSurname(sender?.last_name)].filter(Boolean);
  return parts.length ? parts.join(" ") : null;
}

/** Partículas de apellido («De la Garza», «del Río», «van Dijk»). */
const SURNAME_PARTICLES = new Set([
  "de", "del", "la", "las", "los", "y", "da", "das", "do", "dos", "van", "von", "der", "di", "san", "santa",
]);

/** Primer apellido respetando partículas: se juntan las partículas del
 * inicio hasta la primera palabra que no lo es («De la Garza López» →
 * «De la Garza»; «del Río» → «del Río»; «Pérez López» → «Pérez»). Si todo
 * son partículas, se deja tal cual. */
export function retoFirstSurname(lastName?: string | null): string {
  const words = (lastName ?? "").trim().split(/\s+/).filter(Boolean);
  const out: string[] = [];
  for (const word of words) {
    out.push(word);
    if (!SURNAME_PARTICLES.has(word.toLocaleLowerCase("es"))) break;
  }
  return out.join(" ");
}

const SPORT_WORDS: Record<string, string> = { tennis: "tenis", tenis: "tenis", padel: "pádel", pickleball: "pickleball" };

/** Deporte en palabras para el mensaje (tenis si no se sabe). */
export function retoSportWord(sport?: string | null): string {
  const key = (sport ?? "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return SPORT_WORDS[key] ?? "tenis";
}

/** Texto prellenado del WhatsApp al rival (Eduardo 2026-10-24): sin el
 * nombre del rival (sirve igual para pareja o papá/mamá/tutor de un menor),
 * con el nombre corto de quien escribe y el deporte del torneo. */
export function retoWhatsappMessage(sender: RetoSender | null | undefined, sport?: string | null): string {
  const me = retoSenderName(sender);
  const who = me ? `Hola, soy ${me}.` : "Hola.";
  return `${who} ¿Coordinamos nuestro juego del torneo de ${retoSportWord(sport)}?`;
}

/** «Reportar resultado» con el perfil que juega (cuenta familiar). Un id
 * negativo es un matchup publicado que aún no es partido. */
export function retoReportHref(matchId: number, profileId?: number | null): string {
  return `/player/result-reports?match_id=${matchId}${profileId ? `&player_profile_id=${profileId}` : ""}`;
}
